/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import { Box, Link, SxProps, Theme, Tooltip } from "@mui/material";

type UrlType = {
  urlText?: React.ReactNode;
  urlIconText?: React.ReactNode;
  urlLabel?: string;
  urlPath?: string;
  disabled?: boolean;
  disabledTooltip?: string; // new prop
  underline?: "none" | "hover" | "always";
  clickAction?: () => void;
  sx?: SxProps<Theme>;
};

const Url = ({
  urlText,
  urlIconText,
  urlLabel,
  urlPath,
  clickAction,
  disabled = false,
  disabledTooltip,
  underline = "none",
  sx = {},
}: UrlType) => {
  const isLink = typeof urlPath === "string" && urlPath.trim().length > 0;
  const isButton = !!clickAction && !isLink;

  const linkContent = (
    <Link
      href={isLink ? urlPath : undefined}
      aria-label={typeof urlText === "string" ? urlText : urlLabel}
      role="link"
      component={isLink ? "a" : "button"}
      type={isButton ? "button" : undefined}
      variant="body2"
      underline={underline}
      onClick={disabled ? undefined : clickAction}
      sx={[
        {
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          textDecoration: underline === "always" ? "underline" : "none",
          "&:hover": {
            textDecoration:
              underline === "hover" || underline === "always"
                ? "underline"
                : "none",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {urlIconText && urlText ? (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {urlIconText}
          {urlText}
        </Box>
      ) : (
        urlText || urlIconText
      )}
    </Link>
  );

  return disabled && disabledTooltip ? (
    <Tooltip title={disabledTooltip}>{linkContent}</Tooltip>
  ) : (
    linkContent
  );
};

export default Url;
